import axios from "axios";
import { API_BASE } from "../utils/api";

export interface TranscriptTurn {
  speaker: "Doctor" | "Patient" | "Unknown";
  text: string;
}

export interface StructuredTranscript {
  turns: TranscriptTurn[];
}

export interface SOAPNoteCopyMarkdown {
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
  full: string;
}

// A generated document section value: text, list, or a nested group
export type DocValue = string | string[] | { [key: string]: DocValue };

// A generated document is a (possibly nested) map of section key -> value
export type GeneratedDocument = Record<string, DocValue>;

// Template id: numeric DB id for a custom template, or "soap" for the default
export type TemplateId = number | "soap";

export interface DocumentTemplateOption {
  id: TemplateId;
  name: string;
  doc_type: string;
  is_default: boolean;
}

export interface SOAPNote {
  id: string;
  status: "generating" | "completed" | "failed";
  error_message?: string | null;
  transcript: string;
  structured_transcript?: StructuredTranscript | null;
  subjective: any;
  objective: any;
  assessment: any;
  plan: any;
  // Custom-template output; null/absent for default SOAP notes
  generated_document?: GeneratedDocument | null;
  template?: number | null;
  template_name?: string | null;
  doc_type?: string;
  patient: number | null;
  appointment: number | null;
  doctor: number;
  doctor_name: string;
  generation_time_seconds: number;
  created_at: string;
  updated_at: string;
  has_audio: boolean;
  audio_archive?: AudioArchive;
  copy_markdown?: SOAPNoteCopyMarkdown | null;
}

export interface AudioArchive {
  id: string;
  soap_note: string;
  s3_key: string;
  s3_bucket: string;
  filename: string;
  file_size: number;
  file_size_mb: number;
  content_type: string;
  transcript: string;
  uploaded_at: string;
  download_url: string; // Presigned URL
}

export interface SOAPNoteListItem {
  id: string;
  patient: number | null;
  patient_name: string | null;
  appointment: number | null;
  doctor: number;
  doctor_name: string;
  created_at: string;
  has_audio: boolean;
}

const encountersService = {
  // List doctor's SOAP notes (encounters)
  async fetchDoctorEncounters(params?: {
    start_date?: string;
    end_date?: string;
    patient_id?: number;
    page?: number;
  }): Promise<{ results: SOAPNoteListItem[]; count: number }> {
    const response = await axios.get(`${API_BASE}/soap-notes/`, { params });
    return response.data;
  },

  // Get full SOAP note details with audio
  async fetchEncounterDetails(id: string): Promise<SOAPNote> {
    const response = await axios.get(`${API_BASE}/soap-notes/${id}/`);
    return response.data;
  },

  // Delete a SOAP note (encounter)
  async deleteEncounter(id: string): Promise<void> {
    await axios.delete(`${API_BASE}/soap-notes/${id}/`);
  },

  // Audio at or above this size skips the multipart POST and goes directly to
  // S3 via a presigned POST, so large recordings don't ride through the API.
  LARGE_AUDIO_THRESHOLD_BYTES: 20 * 1024 * 1024,

  // Generate a new encounter from a browser recording: sends the transcript
  // + audio to the shared SOAP pipeline. Returns the created note id with
  // status "generating" — poll with pollUntilComplete.
  //
  // Small audio → single multipart POST to /generate/.
  // Large audio → /generate/ with transcript only, then presigned direct-to-S3
  // upload (audio attaches to the note server-side).
  async generateEncounter(args: {
    transcript: string;
    audioBlob?: Blob | null;
    audioFilename?: string;
    patientId?: number | null;
    appointmentId?: number | null;
    templateId?: TemplateId;
  }): Promise<{ id: string; status: string; created_at: string }> {
    const filename = args.audioFilename || `recording-${Date.now()}.webm`;
    const isLarge =
      !!args.audioBlob &&
      args.audioBlob.size >= encountersService.LARGE_AUDIO_THRESHOLD_BYTES;

    const form = new FormData();
    form.append("transcript", args.transcript);
    if (args.patientId != null) form.append("patient_id", String(args.patientId));
    if (args.appointmentId != null)
      form.append("appointment_id", String(args.appointmentId));
    if (args.templateId !== undefined)
      form.append("template_id", String(args.templateId));
    if (args.audioBlob && !isLarge) {
      form.append("audio_file", args.audioBlob, filename);
    }

    const response = await axios.post(`${API_BASE}/soap-notes/generate/`, form);
    const created = response.data;

    if (args.audioBlob && isLarge) {
      try {
        await encountersService.uploadAudioViaPresignedUrl(
          created.id,
          args.audioBlob,
          filename,
        );
      } catch (err) {
        // Non-fatal: the note still generates from the transcript, it just
        // won't have audio playback attached.
        console.warn("Presigned audio upload failed", err);
      }
    }
    return created;
  },

  // Direct-to-S3 upload for large recordings using the backend's presigned
  // POST (fields include the SSE-KMS conditions the bucket policy requires).
  async uploadAudioViaPresignedUrl(
    soapNoteId: string,
    blob: Blob,
    filename: string,
  ): Promise<void> {
    const { data } = await axios.post(
      `${API_BASE}/soap-notes/audio/presigned-url/`,
      {
        soap_note_id: soapNoteId,
        filename,
        file_size: blob.size,
        content_type: blob.type || "audio/webm",
      },
    );

    const s3Form = new FormData();
    Object.entries(data.upload_fields || {}).forEach(([k, v]) =>
      s3Form.append(k, String(v)),
    );
    s3Form.append("file", blob, filename);

    // Bare client: the app's global axios interceptors would attach the Cognito
    // Bearer header, which S3 rejects on a presigned POST.
    await axios.create().post(data.upload_url, s3Form);
  },

  // Regenerate a note from its stored transcript. Passing templateId switches
  // the template ("soap" for the default SOAP note); omitting it reuses the
  // note's existing template.
  async regenerateEncounter(
    id: string,
    templateId?: TemplateId,
  ): Promise<{ id: string; status: string }> {
    const body = templateId !== undefined ? { template_id: templateId } : undefined;
    const response = await axios.post(
      `${API_BASE}/soap-notes/${id}/regenerate/`,
      body,
    );
    return response.data;
  },

  // List the doctor's document templates (+ synthetic SOAP default entry)
  async fetchTemplates(): Promise<DocumentTemplateOption[]> {
    const response = await axios.get(`${API_BASE}/document-templates/`);
    return response.data;
  },

  // Set the doctor's default template ("soap" to reset to the default SOAP note)
  async setDefaultTemplate(templateId: TemplateId): Promise<{ default: TemplateId }> {
    const response = await axios.post(
      `${API_BASE}/document-templates/set-default/`,
      { template_id: templateId },
    );
    return response.data;
  },

  // A poll is abandoned only after this many *consecutive* failed reads, so a
  // single network blip or token refresh mid-generation doesn't report a
  // successful generation as failed.
  POLL_MAX_CONSECUTIVE_ERRORS: 3,

  // Poll a SOAP note until status is no longer "generating"
  async pollUntilComplete(
    id: string,
    onUpdate?: (status: string) => void,
    intervalMs = 5000,
    maxWaitMs = 600000,
  ): Promise<SOAPNote> {
    const start = Date.now();
    let consecutiveErrors = 0;
    while (Date.now() - start < maxWaitMs) {
      await new Promise((res) => setTimeout(res, intervalMs));

      let data: SOAPNote;
      try {
        data = await encountersService.fetchEncounterDetails(id);
        consecutiveErrors = 0;
      } catch (err) {
        consecutiveErrors += 1;
        if (
          consecutiveErrors >= encountersService.POLL_MAX_CONSECUTIVE_ERRORS
        ) {
          throw err;
        }
        continue;
      }

      onUpdate?.(data.status);
      if (data.status !== "generating") {
        return data;
      }
    }
    throw new Error("SOAP generation timed out");
  },

  // Partially update SOAP note sections (SOAP columns) or a custom-template
  // note's whole generated_document.
  async updateEncounter(
    id: string,
    data: Partial<{
      subjective: any;
      objective: any;
      assessment: any;
      plan: any;
      generated_document: GeneratedDocument;
    }>,
  ): Promise<SOAPNote> {
    const response = await axios.patch(`${API_BASE}/soap-notes/${id}/`, data);
    return response.data;
  },
};

export default encountersService;
